import { useState, useEffect } from "react";
import { ShieldCheck, Loader2, Trash2, RotateCcw, Check, ImageOff, Film } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Moderation() {
  const { user } = useAuth();
  const [passcode, setPasscode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState([]);
  const [threshold, setThreshold] = useState(3);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const loadReports = async (code) => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/reports", { passcode: code || undefined });
      setItems(data.items || []);
      setThreshold(data.threshold || 3);
      setAuthed(true);
    } catch {
      if (!user?.unlimited) toast.error("Incorrect passcode");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  // Logged-in admins skip the passcode gate entirely.
  useEffect(() => {
    if (user?.unlimited) loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.unlimited]);

  const act = async (manual_id, action) => {
    setBusy(`${manual_id}-${action}`);
    try {
      await api.post("/admin/reports/action", { passcode: passcode || undefined, manual_id, action });
      setItems((prev) => prev.filter((i) => i.manual_id !== manual_id));
      toast.success(action === "remove" ? "Removed from board" : action === "restore" ? "Restored" : "Reports dismissed");
    } catch {
      toast.error("Action failed");
    } finally {
      setBusy(null);
    }
  };

  if (!authed) {
    if (user?.unlimited) {
      return (
        <div className="min-h-[50vh] flex items-center justify-center text-[#5d8a52]">
          <Loader2 size={26} className="spin" />
        </div>
      );
    }
    return (
      <div className="max-w-sm mx-auto px-4 pt-20">
        <div className="sms-card p-6 sms-fade-up">
          <h1 className="text-lg font-extrabold flex items-center gap-2 mb-4">
            <ShieldCheck size={18} className="text-[#5d8a52]" /> Moderation
          </h1>
          <form onSubmit={(e) => { e.preventDefault(); loadReports(passcode); }}>
            <input
              data-testid="mod-passcode-input"
              type="password"
              autoComplete="off"
              maxLength={40}
              autoFocus
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Admin passcode"
              className="sms-input w-full px-4 py-3 text-center text-base"
            />
            <button
              data-testid="mod-submit-btn"
              type="submit"
              disabled={loading}
              className="sms-solid-btn w-full text-white font-bold py-3 rounded-2xl mt-3 flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {loading && <Loader2 size={16} className="spin" />} Enter
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 pt-8 pb-16">
      <div className="flex items-center gap-2.5 mb-1">
        <ShieldCheck size={24} className="text-[#5d8a52]" />
        <h1 className="text-2xl font-extrabold">Reported posts</h1>
      </div>
      <p className="text-[#6b6880] mb-6">Posts auto-hide after {threshold} reports. Remove pulls a post from the public board (author keeps it); Restore puts it back; Dismiss clears reports.</p>

      {items.length === 0 ? (
        <div className="sms-card p-12 text-center text-[#6b6880]" data-testid="mod-empty">Nothing to review — the board is clean. 🎉</div>
      ) : (
        <div className="space-y-3" data-testid="mod-list">
          {items.map((it) => (
            <div key={it.manual_id} data-testid={`mod-item-${it.manual_id}`} className="sms-card p-3 flex items-center gap-3">
              <div className="w-16 h-16 rounded-xl bg-[#f1eff8] overflow-hidden shrink-0 relative">
                {it.hero_image_id
                  ? <img src={imageUrl(it.hero_image_id)} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-[#b7b3c8]"><ImageOff size={20} /></div>}
                {it.has_video && <span className="absolute bottom-1 left-1 text-white bg-black/55 rounded-full p-0.5"><Film size={10} /></span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{it.title}</div>
                <div className="text-xs text-[#9a96ac]">by {it.author_name} · {it.report_count} report{it.report_count > 1 ? "s" : ""}{it.hidden ? " · hidden" : ""}</div>
                <div className="text-[11px] text-[#b7b3c8] truncate">{[...new Set(it.reasons)].join(", ")}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button data-testid={`mod-remove-${it.manual_id}`} onClick={() => act(it.manual_id, "remove")} disabled={busy} title="Remove from board"
                  className="p-2 rounded-lg text-[#e11d48] bg-[#fef2f2] hover:bg-[#fee2e4] transition-colors">
                  {busy === `${it.manual_id}-remove` ? <Loader2 size={16} className="spin" /> : <Trash2 size={16} />}
                </button>
                {it.hidden && (
                  <button data-testid={`mod-restore-${it.manual_id}`} onClick={() => act(it.manual_id, "restore")} disabled={busy} title="Restore to board"
                    className="p-2 rounded-lg text-[#5d8a52] bg-[#eef3ea] hover:bg-[#e0ecd9] transition-colors">
                    {busy === `${it.manual_id}-restore` ? <Loader2 size={16} className="spin" /> : <RotateCcw size={16} />}
                  </button>
                )}
                <button data-testid={`mod-dismiss-${it.manual_id}`} onClick={() => act(it.manual_id, "dismiss")} disabled={busy} title="Dismiss reports"
                  className="p-2 rounded-lg text-[#16a34a] bg-[#f0fdf4] hover:bg-[#dcfce7] transition-colors">
                  {busy === `${it.manual_id}-dismiss` ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
